import { Component, Property, Mesh, MeshIndexType, MeshAttribute } from '@wonderlandengine/api';

// ─────────────────────────────────────────────────────────────────────────────
//  WaterSurface
//
//  Tiles a water quad onto every VoxelTreadmill chunk at the computed water Y.
//  Water level is derived from VoxelTreadmill.waterNormY × VoxelTreadmill.terrainHeight
//  so it scales automatically when terrain amplitude changes. No hardcoded -7.
//
//  Each quad is parented to the chunk root so it moves for free as the treadmill
//  scrolls. chunk.root.destroy() kills the child water object automatically.
//
//  EDITOR SETUP (required before this component does anything visible):
//
//   1. Create a Pipeline asset, point Fragment Shader at shaders/Water.frag.
//      Blend Mode → Alpha   |   Depth Write → OFF   |   Culling → Back
//
//   2. Declare these properties on the pipeline (name must match exactly):
//        shallowColor   Color     [0.20, 0.62, 0.78, 1]   teal at grazing angle
//        deepColor      Color     [0.04, 0.14, 0.36, 1]   dark blue straight-down
//        foamColor      Color     [0.90, 0.95, 1.00, 0.6] whitecaps (a = intensity)
//        waveScale      Float     0.25
//        waveSpeed      Float     0.80
//        opacity        Float     0.82
//        waterTime      Float     0.0                      (JS writes each frame)
//        specPower      Float     96.0
//
//   3. Create a Material from that Pipeline.
//
//   4. Add this component to any scene object (an empty object is fine).
//      Assign `waterMaterial`. Water level is driven by VoxelTreadmill automatically.
// ─────────────────────────────────────────────────────────────────────────────

export class WaterSurface extends Component {
    static TypeName = 'water-surface';
    static Properties = {
        /** Material created from the Water pipeline. */
        waterMaterial: Property.material(),
        /** Fraction of terrainHeight at which the water plane sits (0–1).
         *  waterLevelY = -waterNormDepth × terrainHeight.
         *  0.35 → Y ≈ -6.3 with the default terrainHeight of 18. */
        waterNormDepth: Property.float(0.35),
    };

    _mesh        = null;
    _elapsed     = 0;
    _waterLevelY = -6.3; // set from VoxelTreadmill._waterLevelY in _hook()

    start() {
        window.WaterSurface = this;
        const tryHook = () => {
            if (!window.VoxelTreadmill) { setTimeout(tryHook, 200); return; }
            this._hook(window.VoxelTreadmill);
        };
        setTimeout(tryHook, 100);
    }

    update(dt) {
        this._elapsed += dt;
        if (this.waterMaterial) {
            this.waterMaterial.waterTime = this._elapsed;
        }
    }

    // ── Private ────────────────────────────────────────────────────────────────

    _hook(treadmill) {
        const cs = treadmill.chunkSize;

        // Compute from our own property × treadmill's terrain amplitude, then
        // write back into treadmill._waterLevelY so swimming physics matches exactly.
        this._waterLevelY      = -(this.waterNormDepth) * treadmill.terrainHeight;
        treadmill._waterLevelY = this._waterLevelY;

        this._mesh = this._buildMesh(cs);

        const self = this;
        const origBuild = treadmill._buildChunk.bind(treadmill);
        treadmill._buildChunk = function (cx, cz, id) {
            const promise = origBuild(cx, cz, id);
            const chunk   = treadmill._chunks.get(id);
            if (chunk) self._addWaterQuad(chunk.root);
            return promise;
        };

        for (const chunk of treadmill._chunks.values()) {
            this._addWaterQuad(chunk.root);
        }
    }

    _addWaterQuad(root) {
        const waterObj = this.engine.scene.addObject(root);
        waterObj.setPositionLocal([0, this._waterLevelY, 0]);

        const mc    = waterObj.addComponent('mesh');
        mc.mesh     = this._mesh;
        mc.material = this.waterMaterial;
    }

    /**
     * Flat quad covering [0..cs] in local XZ.
     * Winding [0,2,1, 0,3,2] → cross product gives +Y normal (faces up).
     */
    _buildMesh(cs) {
        const mesh = new Mesh(this.engine, {
            indexType: MeshIndexType.UnsignedInt,
            indexData: new Uint32Array([0, 2, 1,  0, 3, 2]),
            vertexCount: 4,
        });

        const pos  = mesh.attribute(MeshAttribute.Position);
        const norm = mesh.attribute(MeshAttribute.Normal);

        pos.set(0, [0,  0,  0 ]);
        pos.set(1, [cs, 0,  0 ]);
        pos.set(2, [cs, 0,  cs]);
        pos.set(3, [0,  0,  cs]);

        for (let i = 0; i < 4; ++i) norm.set(i, [0, 1, 0]);

        mesh.update();
        return mesh;
    }
}
